import { motion } from 'framer-motion'
import { SEO } from '@/components/SEO'
import { siteConfig } from '@/config/site'

export function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy"
        description="NEXAIGEN Privacy Policy - How we collect, use, and protect your personal information."
        url="https://nexaigen.co.uk/privacy"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="pt-32 pb-20 container bg-background min-h-screen"
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-bold mb-8 text-white">Privacy Policy</h1>
          <p className="text-slate-400 mb-12">Last updated: December 2024</p>

          <div className="prose prose-invert prose-slate max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="text-slate-400 leading-relaxed">
                NEXAIGEN ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website nexaigen.co.uk or use our services.
              </p>
              <p className="text-slate-400 leading-relaxed mt-4">
                We are registered in the United Kingdom and comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold text-white mb-3">Personal Information</h3>
              <p className="text-slate-400 leading-relaxed">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Fill out our contact form</li>
                <li>Subscribe to our newsletter</li>
                <li>Request a quote for our services</li>
                <li>Communicate with us via email or phone</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                This information may include your name, email address, phone number, business name, and any other information you choose to provide.
              </p>

              <h3 className="text-xl font-semibold text-white mb-3 mt-6">Automatically Collected Information</h3>
              <p className="text-slate-400 leading-relaxed">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device type</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website address</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
              <p className="text-slate-400 leading-relaxed">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Respond to your enquiries and provide customer support</li>
                <li>Send you quotes and proposals for our services</li>
                <li>Deliver the services you have requested</li>
                <li>Send you marketing communications (with your consent)</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Legal Basis for Processing</h2>
              <p className="text-slate-400 leading-relaxed">
                Under UK GDPR, we process your personal data based on:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li><strong className="text-white">Consent:</strong> When you subscribe to our newsletter or agree to marketing communications</li>
                <li><strong className="text-white">Contract:</strong> When processing is necessary to fulfil a contract with you</li>
                <li><strong className="text-white">Legitimate interests:</strong> For responding to enquiries and improving our services</li>
                <li><strong className="text-white">Legal obligation:</strong> When required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Data Sharing and Third Parties</h2>
              <p className="text-slate-400 leading-relaxed">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Service providers who assist in delivering our services (e.g., hosting providers, email services)</li>
                <li>Professional advisers (e.g., accountants, lawyers) when necessary</li>
                <li>Law enforcement or regulatory bodies when required by law</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                All third parties are required to respect the security of your data and treat it in accordance with the law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Cookies</h2>
              <p className="text-slate-400 leading-relaxed">
                Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device. We use:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li><strong className="text-white">Essential cookies:</strong> Necessary for the website to function properly</li>
                <li><strong className="text-white">Analytics cookies:</strong> To understand how visitors use our website</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling certain cookies may affect website functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Data Retention</h2>
              <p className="text-slate-400 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, including to satisfy legal, accounting, or reporting requirements. Contact form submissions are retained for up to 3 years unless you request earlier deletion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Your Rights</h2>
              <p className="text-slate-400 leading-relaxed">
                Under UK GDPR, you have the right to:
              </p>
              <ul className="list-disc list-inside text-slate-400 mt-4 space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Request restriction of processing</li>
                <li>Request data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-slate-400 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the details below.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Data Security</h2>
              <p className="text-slate-400 leading-relaxed">
                We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Changes to This Policy</h2>
              <p className="text-slate-400 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Contact Us</h2>
              <p className="text-slate-400 leading-relaxed">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 text-slate-400 space-y-2">
                <p><strong className="text-white">Email:</strong> {siteConfig.email}</p>
                <p><strong className="text-white">Phone:</strong> {siteConfig.phone}</p>
                <p><strong className="text-white">Location:</strong> {siteConfig.address.city}, {siteConfig.address.country}</p>
              </div>
              <p className="text-slate-400 leading-relaxed mt-4">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you believe your data protection rights have been violated.
              </p>
            </section>
          </div>
        </div>
      </motion.div>
    </>
  )
}

export default Privacy
